import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';
import { Form, Formik } from 'formik';

import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikTextRow from 'components/Common/Formik/Row/FormikTextRow';
import Trans from 'components/Common/Translation/Trans';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import Button from 'components/UI/Buttons/Button/Button';
import Loading from 'components/Common/Loading/Loading';
import ForgotPasswordLink, {
  Props as ForgotPasswordLinkProps,
} from './Links/ForgotPasswordLink';
import ForgotCustomerNumberLink, {
  Props as ForgotCustomerNumberLinkProps,
} from './Links/ForgotCustomerNumberLink';
import { LoginValues, useCustomer } from 'hooks/useCustomer';
import { validationSchema } from 'schemas/SignInWithEmailErrors';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { getRoutesUri } from 'services/generic';
import { AC } from 'settings/form';

import styles from './LoginForm.module.scss';

export type Props = {
  isCheckoutPage: boolean;
  classes?: {
    container?: string;
    forgotCustomerNumberLink?: ForgotCustomerNumberLinkProps['classes'];
    forgotPasswordLink?: ForgotPasswordLinkProps['classes'];
  };
};

export default function LoginForm({
  isCheckoutPage = false,
  classes,
}: Props): JSX.Element {
  const { t } = useTranslation(['loginPage']);
  const { login } = useCustomer();
  const navigate = useNavigate();
  const turnstile = useRef<TurnstileRef>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const isTurnstileDesktop = useMediaQuery({ minWidth: '359px' });
  const [errorMessages, setErrorMessages] = useState<
    Record<string, string | React.ReactElement>
  >();

  function resetErrorMessages() {
    if (errorMessages) {
      setErrorMessages({});
    }
  }

  function onSubmitButton() {
    pushGtmEvent({
      event: '_login',
      user_login_type: isCheckoutPage ? 'checkout' : 'compte',
    });
  }

  async function onSubmit(values: LoginValues) {
    setLoading(true);

    const response = await login(values);

    if (response.statusCode === 200) {
      localStorage.setItem(
        'user_login_type',
        isCheckoutPage ? 'checkout' : 'compte'
      );

      navigate(
        isCheckoutPage
          ? getRoutesUri<string>('checkoutShipping')
          : getRoutesUri<string>('customerAccountOrderHistory')
      );

      return;
    }

    try {
      if (response.error) {
        setErrorMessages({ global: response.error });
      } else if (response.errors) {
        setErrorMessages(response.errors);
      } else {
        setErrorMessages({
          other: (
            <Trans
              i18nKey={'LABEL_ERROR_INVALID_LOGIN'}
              namespace={'loginPage'}
            />
          ),
        });
      }
    } finally {
      turnstile.current?.reset();
      setLoading(false);
    }
  }

  return (
    <>
      <Formik
        initialValues={{ email: '', password: '', turnstileToken: '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Form
          data-testid={'login-form'}
          className={classNames(styles.formContainer, classes?.container)}
          noValidate
        >
          <FormikErrorsGtmLogger
            page_type="identification"
            form_type="connexion"
          />
          <FormikTextRow
            key={'emailField'}
            classes={{
              label: styles.inputLabel,
            }}
            inputProps={{
              name: 'email',
              placeholder: t('LABEL_PLACEHOLDER_IDENTIFIER'),
              autoComplete: AC.Email,
              required: true,
              model: 'secondary',
            }}
            afterOnChange={() => resetErrorMessages()}
            label={
              <Trans
                i18nKey={'LABEL_EMAIL_OR_NUMBER_CLIENT'}
                namespace={'loginPage'}
                additionalComponents={{
                  e: <i className={styles.asterisk} />,
                }}
              />
            }
          />
          <ForgotCustomerNumberLink
            classes={{
              link: classes?.forgotCustomerNumberLink?.link,
              wrapper: classNames(
                styles.forgotCustomerNumber,
                classes?.forgotCustomerNumberLink?.wrapper
              ),
            }}
          />
          <FormikTextRow
            inputProps={{
              type: 'password',
              name: 'password',
              placeholder: t('LABEL_PLACEHOLDER_PASSWORD'),
              autoComplete: AC.Password,
              required: true,
              model: 'secondary',
            }}
            afterOnChange={() => resetErrorMessages()}
            label={t('LABEL_PASSWORD')}
          />
          <ForgotPasswordLink
            isCheckoutPage={isCheckoutPage}
            classes={{
              link: classes?.forgotPasswordLink?.link,
              wrapper: classNames(
                styles.forgotCustomerPassword,
                classes?.forgotPasswordLink?.wrapper
              ),
            }}
          />
          <TurnstileFormikRow
            turnstileRef={turnstile}
            turnstileProps={{
              className: styles.turnstileWidget,
              options: {
                action: 'login',
                size: isTurnstileDesktop ? 'flexible' : 'compact',
              },
            }}
          />
          {Object.keys(errorMessages || {}).length > 0 && (
            <div className={styles.formErrorContainer}>
              {Object.values(errorMessages || {}).map((errorMessage, key) => (
                <div className={styles.formError} key={key}>
                  {errorMessage}
                </div>
              ))}
            </div>
          )}
          <Button
            type={'submit'}
            model={'primary'}
            onClick={onSubmitButton}
            className={styles.submitButton}
          >
            {t('LABEL_SIGNIN_BUTTON')}
          </Button>
          <ScrollToFieldError />
        </Form>
      </Formik>
      {isLoading && <Loading loading />}
    </>
  );
}
